import { Body, Button, colors, Dialog, Form, Spacer } from '@spritz-finance/ui'
import React from 'react'
import { SubmitHandler } from 'react-hook-form'
import styled, { css } from 'styled-components'
import { Column } from '../../../../components/Layout/Column'
import { Row } from '../../../../components/Layout/Row'
import { BillType, useUpdateBillTypeMutation } from '../../../../graphql/generated/graphql'
import { allBillTypes } from '../../utils/merchantBillTypes'
import { InstitutionImage } from '../InstitutionImage'
import { BillTypeSelector } from '../linkBill/AddAccountForm'
import { Bill } from './Bills'

const RenameAccountContainer = styled.div<{ $removed?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: opacity 0.2s ease-out;
    ${({ $removed }) =>
        $removed &&
        css`
            opacity: 0;
        `};
`

interface RenameFormData {
    billType: BillType
}

const TopWrapper = styled.div`
    width: 100%;
    display: flex;
`

interface ChangeBillTypeDialogProps {
    bill: Bill | null
    onClose: () => void
}

export const ChangeBillTypeDialog: React.VFC<ChangeBillTypeDialogProps> = ({ onClose, bill }) => {
    const [changeBillType, { loading }] = useUpdateBillTypeMutation({
        optimisticResponse: (vars) => {
            return {
                updateBillType: {
                    ...bill!,
                    type: vars.type,
                },
            }
        },
    })

    const handleCloseDialog = () => {
        if (loading) return
        onClose()
    }

    const onSubmit: SubmitHandler<RenameFormData> = async (data) => {
        changeBillType({
            variables: {
                type: data.billType,
                provider: bill?.provider,
                accountId: bill!.accountId,
            },
        })
        onClose()
    }

    return (
        <Dialog open={!!bill} onClose={handleCloseDialog}>
            <Dialog.Content>
                <Dialog.Title>Change bill category</Dialog.Title>
                <RenameAccountContainer $removed={false}>
                    <Spacer height={16} />
                    <Body.Bold size={18}>Want to categorize your bills?</Body.Bold>
                    <Spacer height={8} />
                    <Body.Reg size={14}>
                        Don’t worry, this doesn’t change anything about your billing account
                    </Body.Reg>
                    <Spacer height={32} />
                    <TopWrapper>
                        <Row>
                            <InstitutionImage institution={bill?.institution} />
                        </Row>
                        <Spacer width={16} />
                        <Column>
                            <Body.SmBold color={colors.charcoal} size={14}>
                                {bill?.institution?.name}
                            </Body.SmBold>
                            {bill?.originalName && (
                                <>
                                    <Spacer height={4} />
                                    <Body.SmBold size={14}>{bill?.originalName}</Body.SmBold>
                                </>
                            )}
                        </Column>
                    </TopWrapper>

                    <Spacer height={24} />
                    <Form<RenameFormData>
                        width="100%"
                        onSubmit={onSubmit}
                        initialValues={{ billType: bill?.type }}
                    >
                        <BillTypeSelector options={allBillTypes} />
                        <Spacer height={8} />
                        <Row align="center">
                            <Button.CTA type="submit" disabled={loading} responsive>
                                Update type
                            </Button.CTA>
                        </Row>
                    </Form>
                    <Spacer height={24} />
                </RenameAccountContainer>
            </Dialog.Content>
        </Dialog>
    )
}
